import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { cn } from "../lib/utils";
import { differenceInDays, format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Plus,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Check,
  X,
  MessageCircle,
  Pencil,
  Trash2,
  PartyPopper,
  Gift,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { getEmployees } from "../api/employees";
import {
  getLeaves,
  applyLeave,
  applyCompensatoryEarn,
  applyMaternityEarn,
  applyPaternityEarn,
  cancelLeave,
  getLeaveBalance,
  updateLeaveStatus,
  getOverdraftPreview,
  updateOverdraftDisposition,
  type OverdraftPreview,
  type OverdraftDisposition,
  type MakeupPeriod,
  getMyLeaves
} from "../api/leaves";
import {
  getLeaveSettings,
  getLeaveBalance as getStructuredLeaveBalance,
  type LeaveSettings as LeaveSettingsData,
  type LeaveBalance as StructuredBalance,
  type LeaveType,
} from "../api/leaveSettings";
import api from "../api/api";
import {
  EMPTY_HALF_DAY,
  canOfferHalfDay,
  checkHalfDay,
  defaultSession,
  sessionLabel,
  expectedAtWorkText,
  type HalfDayConfig,
} from "./leaveHalfDay";
import { useAuth } from "../contexts/AuthContext";
import { useAccess } from "../contexts/AccessContext";
import { useToast } from "../hooks/useToast";
import { Leave } from "../types";
import PersonAvatar from "../components/people/PersonAvatar";

type Holiday = { date: string; name: string };

type ApplyErrorState = {
  open: boolean;
  message: string;
  balance_remaining?: number;
  leave_type?: string;
};

/** The next six months, as values the server accepts, starting at `suggested`. */
/** Same shape MyPayroll states these amounts in, so the two sides read alike. */
const rupees = (n?: number | null) => `₹${(n ?? 0).toLocaleString()}`;

function monthOptions(suggested?: string, sameMonthFrom?: string | null) {
  const start = new Date();
  if (suggested) {
    const [y, m] = suggested.split("-").map(Number);
    if (y && m) start.setFullYear(y, m - 1, 1);
  }
  start.setDate(1);
  // The leave's own month does not run from the 1st — it runs from the day
  // after the leave ended, since hours worked before it cannot pay for it. Say
  // so in the label, or the approver picks "October" believing they gave the
  // whole of it.
  const sameMonth = sameMonthFrom ? sameMonthFrom.slice(0, 7) : null;
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return {
      value,
      label:
        value === sameMonth && sameMonthFrom
          ? `${format(d, "MMMM yyyy")} (from ${format(
              new Date(`${sameMonthFrom}T00:00:00`),
              "d MMM"
            )})`
          : format(d, "MMMM yyyy"),
    };
  });
}

/** What an overtime-as-leave request says on the card. */
function conversionChip(c: NonNullable<Leave["conversion"]>) {
  if (c.state === "settled") {
    return c.credited_days
      ? `${c.hours_converted}h → ${c.credited_days} day(s) at ${c.rate}x`
      : "No overtime found in the period";
  }
  const asked = c.hours_requested ? `${c.hours_requested}h` : "all overtime";
  return `${asked} at ${c.rate}x · credits when the period closes`;
}

/** What an already-decided overdraft says on the card. */
function overdraftChip(leave: Leave) {
  const days = `${leave.unpaidDays} day${leave.unpaidDays === 1 ? "" : "s"} unpaid`;
  if (leave.overdraftDisposition === "no_deduction") return `${days} · no deduction`;
  if (leave.overdraftDisposition !== "carry_forward") return days;
  switch (leave.obligationState) {
    case "fulfilled":
      return `${days} · worked back`;
    case "shortfall":
      return `${days} · deducted`;
    case "cancelled":
      return days;
    default: {
      // In whichever unit the employee chose to settle: showing the hours to
      // somebody working off an amount describes a debt that is not theirs.
      const owed =
        leave.recoveryChoice === "money"
          ? `earn ${rupees(leave.makeupAmount)}`
          : `work ${leave.makeupHours ?? 0}h`;
      return `${days} · ${owed} back by ${
        leave.makeupTo ? format(new Date(`${leave.makeupTo}T00:00:00`), "d MMM") : "—"
      }`;
    }
  }
}

export function Leaves() {
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [allLeaves, setAllLeaves] = useState<Leave[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [approvalAction, setApprovalAction] = useState<"approved" | "rejected">(
    "approved"
  );
  const [approvalComments, setApprovalComments] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterEmployee, setFilterEmployee] = useState<string>("all");
  const [employeeFilterOpen, setEmployeeFilterOpen] = useState<boolean>(false);
  // employeeID -> full "First Last" name, fetched from the roster so the
  // Employee filter shows full names (leave records only store a first name).
  const [employeeNameMap, setEmployeeNameMap] = useState<Record<string, string>>(
    {},
  );
  // Per-tab pagination (1-based). My Leaves, Pending Approvals, All Team.
  const [myLeavesPage, setMyLeavesPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [allTeamPage, setAllTeamPage] = useState(1);
  const [newLeave, setNewLeave] = useState({
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",


  });
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const { user } = useAuth();
  const { toast } = useToast();
  const [managerID, setManagerID] = useState<string | null>(null);
  const [employeeID, setEmployeeID] = useState<string | null>(null);
  const [epmtID, setEpmtID] = useState<string | null>(null);
  // Used to gate the maternity / paternity earn buttons. "female" → only
  // maternity is visible; "male" → only paternity. Falls back to showing
  // neither when the gender is unknown / unset.
  const [userGender, setUserGender] = useState<string | null>(null);
  // Half-day leave (policy rule `leave.half_day`). The server says whether it
  // is offered and which halves are allowed — a branch may permit leaving early
  // but not arriving late, so this is a list, not a boolean.
  const [halfDayConfig, setHalfDayConfig] =
    useState<HalfDayConfig>(EMPTY_HALF_DAY);
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDaySession, setHalfDaySession] = useState<string>("");
  const canTakeHalfDay = canOfferHalfDay(halfDayConfig);
  // Supervisor cannot approve, so the "Pending Approvals" tab is hidden for
  // them — default their view to the all-team-requests tab instead.
  // Employees only have My Leaves / Holidays — land them on My Leaves.
  const [activeTab, setActiveTab] = useState(
    user?.role === "supervisor"
      ? "all"
      : (user?.role === "employee" || user?.role === "intern")
        ? "my"
        : "pending",
  );
  const [tabLoading, setTabLoading] = useState(false);
  const [leaveSettings, setLeaveSettings] = useState<LeaveSettingsData | null>(null);
  const [structuredBalance, setStructuredBalance] = useState<StructuredBalance | null>(null);
  const [applyError, setApplyError] = useState<ApplyErrorState>({ open: false, message: "" });

  // Days the leave being decided runs past the employee's balance, and which
  // currency the approver picked for them. Null means entitlement covers it —
  // the ordinary case, and then the approve dialog is unchanged.
  const [overdraft, setOverdraft] = useState<OverdraftPreview | null>(null);
  const [overdraftLoading, setOverdraftLoading] = useState(false);
  const [disposition, setDisposition] = useState<OverdraftDisposition | null>(null);
  // The period a carry-forward has to be worked back in. A whole month by
  // default — it is what people actually say ("work it back in November") —
  // with exact dates available for anyone who needs them.
  const [makeupMonth, setMakeupMonth] = useState("");
  const [makeupExact, setMakeupExact] = useState(false);
  const [makeupFrom, setMakeupFrom] = useState("");
  const [makeupTo, setMakeupTo] = useState("");
  // Set when the dialog is open to CHANGE a decision rather than to approve.
  const [decisionOnly, setDecisionOnly] = useState(false);

  // Compensatory earn-request dialog state — separate from the regular
  // Apply-Leave dialog because it doesn't draw down a balance. Same
  // shape as maternity/paternity: employee picks from/to dates + days.
  // Months whose payroll is approved. An approved compensatory claim re-scores
  // the day it names, so a claim into a processed month would pay that day
  // twice — the server refuses it, and this is what lets the form say why
  // before anything is typed.
  const [payrollLockedMonths, setPayrollLockedMonths] = useState<string[]>([]);
  const [isCompApplyOpen, setIsCompApplyOpen] = useState(false);
  const [compFromDate, setCompFromDate] = useState("");
  const [compToDate, setCompToDate] = useState("");
  const [compDays, setCompDays] = useState<string>("");
  const [compReason, setCompReason] = useState("");
  const [compSubmitting, setCompSubmitting] = useState(false);

  // Maternity / paternity earn-request dialog state — same shape: ask
  // a manager to grant N days of entitlement that lands in the balance
  // on approval, then the employee files normal maternity/paternity
  // leave against that balance.
  const [isMatApplyOpen, setIsMatApplyOpen] = useState(false);
  const [matFromDate, setMatFromDate] = useState("");
  const [matToDate, setMatToDate] = useState("");
  const [matDays, setMatDays] = useState<string>("");
  const [matReason, setMatReason] = useState("");
  const [matSubmitting, setMatSubmitting] = useState(false);

  const [isPatApplyOpen, setIsPatApplyOpen] = useState(false);
  const [patFromDate, setPatFromDate] = useState("");
  const [patToDate, setPatToDate] = useState("");
  const [patDays, setPatDays] = useState<string>("");
  const [patReason, setPatReason] = useState("");
  const [patSubmitting, setPatSubmitting] = useState(false);


  // ── Access-driven gating for the Leaves sub-tabs ──────────────────────
  // Each tab is a leveled feature under the "leaves" panel (see panelCatalog),
  // and Access Management is the ONLY thing that decides them — no role is
  // hardcoded. A key the profile doesn't define falls back to the catalog's
  // declared default (isPanelVisible / canManage handle that), never to a role.
  const { isPanelVisible, canManage } = useAccess();

  // Tab visibility.
  const showMyTab = isPanelVisible("leaves-my");
  const showApprovalsTab = isPanelVisible("leaves-approvals");
  const showTeamTab = isPanelVisible("leaves-team");
  const showHolidaysTab = isPanelVisible("leaves-holidays");
  // Whether to render the team-oriented multi-tab layout vs the employee layout.
  const showTeamLayout = showApprovalsTab || showTeamTab;

  // Manage capabilities within each tab.
  const canApplyOwn = canManage("leaves-my");
  const canApprove = canManage("leaves-approvals");
  // Approve directly from the All-Team list.
  const canApproveFromTeam = canManage("leaves-team");
  const canManageHolidays = canManage("leaves-holidays");

  // Back-compat aliases used throughout the JSX / data-fetch below.
  const isManager = canApprove;          // "acts on others' leaves"
  const canEditHolidays = canManageHolidays;
  const isTeamViewer = showTeamLayout;   // "sees the team-oriented layout"

  // Keep the selected tab pointing at something visible. The role-based default
  // (set in useState below) may be hidden by an Access profile, and permissions
  // hydrate asynchronously — so re-clamp whenever visibility changes.
  useEffect(() => {
    if (!showTeamLayout) return; // employee layout clamps its own value inline
    const visible = [
      [showApprovalsTab, "pending"],
      [showTeamTab, "all"],
      [showMyTab, "my"],
      [showHolidaysTab, "holidays"],
    ]
      .filter(([v]) => v)
      .map(([, k]) => k as string);
    if (visible.length && !visible.includes(activeTab)) setActiveTab(visible[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTeamLayout, showApprovalsTab, showTeamTab, showMyTab, showHolidaysTab, activeTab]);

  // ── Holidays tab state ────────────────────────────────────────────────
  // Pulled from the same salary-settings endpoint that used to live on the
  // Payroll Settings page. The full settings blob is preserved so we can
  // round-trip it on save without dropping the leave-policy fields that now
  // live in Company Settings.
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayName, setHolidayName] = useState("");
  const [editingHolidayKey, setEditingHolidayKey] = useState<string | null>(null);
  const [holidayError, setHolidayError] = useState("");
  const [holidaySaving, setHolidaySaving] = useState(false);
  const salarySettingsRef = useRef<Record<string, any> | null>(null);

  const normalizeHolidayDates = (list: any[] = []): Holiday[] =>
    list.map((h) =>
      typeof h === "string" ? { date: h, name: "Holiday" } : { date: h.date, name: h.name || "Holiday" },
    );

  const fetchHolidays = async () => {
    try {
      setHolidaysLoading(true);
      const { data } = await api.get("/salary/get_salary_settings");
      if (data?.success) {
        salarySettingsRef.current = data.data || {};
        setHolidays(normalizeHolidayDates(data.data?.holiday_dates));
      }
    } catch (err) {
      console.error("Failed to load holidays", err);
    } finally {
      setHolidaysLoading(false);
    }
  };

  const persistHolidays = async (next: Holiday[]) => {
    setHolidaySaving(true);
    try {
      // Round-trip the rest of the salary settings so we don't blow away the
      // leave-policy fields that now live on the Company Settings page.
      const base = salarySettingsRef.current || {};
      const payload = {
        deduction: base.deduction ?? false,
        carryForward: base.carryForward ?? false,
        daysAllowable: base.daysAllowable ?? 0,
        hourly: base.hourly ?? false,
        monthly: base.monthly ?? true,
        sunday: base.sunday ?? false,
        saturday: base.saturday ?? false,
        lateDeduction: base.lateDeduction ?? false,
        overtimeDeduction: base.overtimeDeduction ?? false,
        rateofOvertime: base.rateofOvertime ?? 1.0,
        allowance: base.allowance ?? false,
        holiday_dates: next,
      };
      const { data } = await api.post("/salary/create_or_update_salary_settings", payload);
      if (data?.success) {
        salarySettingsRef.current = data.data || salarySettingsRef.current;
        setHolidays(normalizeHolidayDates(data.data?.holiday_dates ?? next));
      }
      return true;
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast({
        title: "Couldn't save holiday",
        description: typeof detail === "string" ? detail : err?.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setHolidaySaving(false);
    }
  };

  const resetHolidayForm = () => {
    setHolidayDate("");
    setHolidayName("");
    setEditingHolidayKey(null);
    setHolidayError("");
  };

  const handleSaveHoliday = async () => {
    if (!holidayDate || !holidayName.trim()) {
      setHolidayError("Pick a date and enter a holiday name.");
      return;
    }
    const trimmed = holidayName.trim();
    let next: Holiday[];

    if (editingHolidayKey) {
      const clash = holidays.some(
        (h) => h.date === holidayDate && h.date !== editingHolidayKey,
      );
      if (clash) {
        setHolidayError(`A holiday on ${holidayDate} already exists.`);
        return;
      }
      next = holidays.map((h) =>
        h.date === editingHolidayKey ? { date: holidayDate, name: trimmed } : h,
      );
    } else {
      if (holidays.some((h) => h.date === holidayDate)) {
        setHolidayError(`A holiday on ${holidayDate} already exists.`);
        return;
      }
      next = [...holidays, { date: holidayDate, name: trimmed }];
    }

    const ok = await persistHolidays(next);
    if (ok) {
      resetHolidayForm();
      toast({
        title: editingHolidayKey ? "Holiday updated" : "Holiday added",
        description: `${trimmed} — ${holidayDate}`,
      });
    }
  };

  const handleEditHoliday = (h: Holiday) => {
    setEditingHolidayKey(h.date);
    setHolidayDate(h.date);
    setHolidayName(h.name);
    setHolidayError("");
  };

  const handleRemoveHoliday = async (date: string) => {
    if (editingHolidayKey === date) resetHolidayForm();
    const next = holidays.filter((h) => h.date !== date);
    const ok = await persistHolidays(next);
    if (ok) {
      toast({ title: "Holiday removed", description: date });
    }
  };

  useEffect(() => {
    if (activeTab === "holidays") {
      fetchHolidays();
    }
  }, [activeTab]);



  const initLeavesPage = async () => {
    try {
      setLoading(true);

      let context: any = {};

      // Everyone with an Employee row resolves their leave context here — this
      // is where employeeID / epmtID come from, and apply_leave rejects the
      // request outright if they are null. Listing the roles individually meant
      // a role created in Settings → Roles skipped the call entirely and every
      // submission 422'd on the nulls, so gate on the admin tier instead: only
      // admins have no Employee row for the endpoint to find.
      const roleKey = (user?.role || "").toLowerCase();
      if (roleKey !== "admin" && roleKey !== "superadmin") {
        try {
          context = await getLeaveBalance();

          setLeaveBalance(context.balance || {});
          setManagerID(context.managerID);
          setEmployeeID(context.employeeID);
          setEpmtID(context.epmtID);
          setUserGender(context.gender || null);
          setHalfDayConfig(context.halfDay || EMPTY_HALF_DAY);
          setPayrollLockedMonths(context.payrollLockedMonths || []);
        } catch (e) {
          // No employee record (or the lookup failed) — leave the page usable
          // rather than blanking it; the balances just stay empty.
          console.warn("leave context fetch failed", e);
        }
      }

      try {
        const role = user?.role;
        const [settings, balance] = await Promise.all([
          getLeaveSettings(),
          role === "admin" ? Promise.resolve(null) : getStructuredLeaveBalance(),
        ]);
        setLeaveSettings(settings);
        if (balance) setStructuredBalance(balance);
      } catch (e) {
        console.warn("leave settings/balance fetch failed", e);
      }

      let query = "";

      if (user.role === "admin") {
        query = "";
      } else {
        query = context.managerID;
      }

      console.log("ROLE:", user.role);
      console.log("QUERY SENT TO getLeaves:", query);

      const response = await getLeaves(query);

      if (isTeamViewer) {
        setAllLeaves(response.leaves);
        setLeaves(
          response.leaves.filter(
            (leave) => leave.employeeId === user?._id
          )
        );
      } else {
        setLeaves(response.leaves);
      }

    } catch (err) {
      console.error("initLeavesPage error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initLeavesPage();
  }, []);

  // Load the roster to resolve full names for the All-Team Employee filter.
  // /employeesAll is role-scoped, so only fetch when the team tab is visible.
  useEffect(() => {
    if (!showTeamTab) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getEmployees({ include_peers: true, limit: 100 });
        const rows: any[] = res?.employees || [];
        const map: Record<string, string> = {};
        rows.forEach((emp) => {
          const id = emp.employeeID;
          if (!id) return;
          const full = `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
          if (full) map[id] = full;
        });
        if (!cancelled) setEmployeeNameMap(map);
      } catch (e) {
        console.warn("employee roster fetch for leave filter failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showTeamTab]);


  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await getLeaves("");


      if (isTeamViewer) {
        // Managers / supervisors see team leaves
        setAllLeaves(response.leaves);
        // Also set user's own leaves for the "My Leaves" tab
        setLeaves(
          response.leaves.filter((leave) => leave.employeeId === user?._id)
        );
      } else {
        // Employees see only their own leaves
        setLeaves(response.leaves);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMyLeaves = async () => {
    try {
      setTabLoading(true);
      const res = await getMyLeaves();
      setLeaves(res.Leaves);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch my leaves",
        variant: "destructive",
      });
    } finally {
      setTabLoading(false);
    }
  };

  // useEffect(() => {
  //     fetchMyLeaves();
  //   }, []);
  useEffect(() => {
    if (activeTab === "my") {
      fetchMyLeaves();
    }
  }, [activeTab]);



  const fetchLeaveBalance = async () => {
    try {
      if (user?._id) {
        const response = await getLeaveBalance(user._id);
        setLeaveBalance(response.balance);
      }
    } catch (error) {
      console.error("Error fetching leave balance:", error);
    }
  };

  // Wipe every field of the Apply Leave form. Called from every close path
  // (Cancel button, X / Esc / backdrop via onOpenChange, successful submit)
  // so the next time the user opens "Apply for Leave" they start fresh.
  const resetApplyForm = () => {
    setIsHalfDay(false);
    setHalfDaySession("");
    setNewLeave({ leaveType: "", startDate: "", endDate: "", reason: "" });
    setStartDate(undefined);
    setEndDate(undefined);
    setStartOpen(false);
    setEndOpen(false);
    setApplyError({ open: false, message: "" });
  };

  const handleApplyLeave = async () => {
    try {
      if (!startDate) {
        toast({
          title: "Error",
          description: "Please select a start date first",
          variant: "destructive",
        });
        return;
      }
      if (!endDate) {
        toast({
          title: "Error",
          description: "Please select an end date",
          variant: "destructive",
        });
        return;
      }
      if (endDate < startDate) {
        toast({
          title: "Error",
          description: "End date must be on or after the start date",
          variant: "destructive",
        });
        return;
      }

      // The leave context (see initLeavesPage) is what fills these in. If it
      // failed to load they are still null, and the API rejects the payload
      // with a raw validation error that says nothing useful — say what is
      // actually wrong instead of firing a request that cannot succeed.
      if (!employeeID || !epmtID) {
        toast({
          title: "Failed",
          description:
            "Your employee profile could not be loaded. Refresh the page and try again.",
          variant: "destructive",
        });
        return;
      }

      // Guards only — the server re-validates all of this. They exist so the
      // employee is told on the spot instead of via a failed request.
      const halfDay = checkHalfDay({
        active: canTakeHalfDay && isHalfDay,
        session: halfDaySession,
        spanDays: differenceInDays(endDate, startDate) + 1,
      });
      if (!halfDay.ok) {
        toast({
          title: halfDay.title,
          description: halfDay.description,
          variant: "destructive",
        });
        return;
      }

      const leaveData = {
        employeeID: employeeID,
        managerID: managerID || "",
        epmtID: epmtID,
        name: user.name,
        fromDate: format(startDate, "yyyy-MM-dd"),
        toDate: format(endDate, "yyyy-MM-dd"),
        // 0.5 for a half day. The server recomputes this from the session
        // rather than trusting it, so the two can never disagree.
        totalDays: halfDay.totalDays,
        leaveType: newLeave.leaveType,
        reason: newLeave.reason,
        status: "PENDING",
        appliedOn: new Date().toISOString(),
        ...(halfDay.session ? { session: halfDay.session } : {}),
      };

      const applied = await applyLeave(leaveData);

      // The request went through. If some of it ran past the balance, say so
      // here rather than letting the employee find out when the payslip is
      // short: it is their approver who decides which way it is paid for.
      const warning = applied?.overdraft_warning;
      if (warning?.unpaid_days) {
        toast({
          title: "Submitted — some days are unpaid",
          description:
            warning.message ||
            `${warning.unpaid_days} day(s) are beyond your balance.`,
          duration: 8000,
        });
      } else {
        toast({
          title: "Success",
          description: "Leave request submitted successfully",
        });
      }

      setIsApplyDialogOpen(false);
      resetApplyForm();

      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setApplyError({
          open: true,
          message: detail.detail || detail.message || "Leave request blocked",
          balance_remaining: detail.balance_remaining,
          leave_type: detail.leave_type,
        });
        return;
      }
      const message =
        typeof detail === "string" ? detail : error?.message || "Failed to submit leave";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const resetCompForm = () => {
    setCompFromDate("");
    setCompToDate("");
    setCompDays("");
    setCompReason("");
  };

  const resetMatForm = () => {
    setMatFromDate("");
    setMatToDate("");
    setMatDays("");
    setMatReason("");
  };

  const resetPatForm = () => {
    setPatFromDate("");
    setPatToDate("");
    setPatDays("");
    setPatReason("");
  };

  // Compute totalDays from from/to dates whenever they change so the
  // helper text + final submission stay in sync.
  const daysBetween = (from: string, to: string): number => {
    if (!from || !to) return 0;
    const f = new Date(from);
    const t = new Date(to);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return 0;
    const diff = differenceInDays(t, f);
    return diff >= 0 ? diff + 1 : 0;
  };

  const handleApplyParentalEarn = async (
    kind: "maternity" | "paternity",
  ) => {
    const isMat = kind === "maternity";
    const fromDate = isMat ? matFromDate : patFromDate;
    const toDate = isMat ? matToDate : patToDate;
    const daysStr = isMat ? matDays : patDays;
    const reason = isMat ? matReason : patReason;
    const setSubmitting = isMat ? setMatSubmitting : setPatSubmitting;
    const setOpen = isMat ? setIsMatApplyOpen : setIsPatApplyOpen;
    const resetForm = isMat ? resetMatForm : resetPatForm;
    const apiFn = isMat ? applyMaternityEarn : applyPaternityEarn;
    const label = isMat ? "Maternity" : "Paternity";

    if (!fromDate || !toDate) {
      toast({
        title: "Dates required",
        description: "Please select both a from and to date.",
        variant: "destructive",
      });
      return;
    }

    const computedDays = daysBetween(fromDate, toDate);
    const requestedDays = Number(daysStr) > 0 ? Number(daysStr) : computedDays;
    if (requestedDays <= 0) {
      toast({
        title: "Days required",
        description: "Days must be a positive number.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);
      await apiFn({
        employeeID,
        managerID,
        epmtID,
        name: user.name,
        fromDate,
        toDate,
        days: requestedDays,
        reason: reason || undefined,
      });
      toast({
        title: `${label} leave request submitted`,
        description:
          "Your reporting person will review and approve. Once approved, the days will appear in your balance.",
      });
      setOpen(false);
      resetForm();
      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "object" && detail?.detail
          ? detail.detail
          : typeof detail === "string"
            ? detail
            : error?.message || `Failed to submit ${label.toLowerCase()} request`;
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // The processed months the chosen range runs into, named for the warning.
  const compLockedMonths = useMemo(() => {
    if (!compFromDate || !payrollLockedMonths.length) return [];
    const last = compToDate || compFromDate;
    const touched = new Set<string>();
    // Walk month by month off the first of each month: adding days would skip
    // a month on a long range and mis-step across DST.
    const cursor = new Date(`${compFromDate.slice(0, 7)}-01T00:00:00`);
    const end = new Date(`${last.slice(0, 7)}-01T00:00:00`);
    if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return [];
    while (cursor <= end) {
      touched.add(
        `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`,
      );
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return payrollLockedMonths.filter((month) => touched.has(month));
  }, [compFromDate, compToDate, payrollLockedMonths]);

  const compLockedLabel = compLockedMonths
    .map((month) => {
      const at = new Date(`${month}-01T00:00:00`);
      return Number.isNaN(at.getTime())
        ? month
        : at.toLocaleDateString([], { month: "long", year: "numeric" });
    })
    .join(", ");

  const handleApplyCompensatory = async () => {
    if (!compFromDate || !compToDate) {
      toast({
        title: "Dates required",
        description: "Please select both a from and to date.",
        variant: "destructive",
      });
      return;
    }

    if (compLockedMonths.length) {
      toast({
        title: "Salary already processed",
        description: `The salary for ${compLockedLabel} is already processed, so compensatory leave can no longer be claimed for those days.`,
        variant: "destructive",
      });
      return;
    }

    const computedDays = daysBetween(compFromDate, compToDate);
    const requestedDays = Number(compDays) > 0 ? Number(compDays) : computedDays;
    if (requestedDays <= 0) {
      toast({
        title: "Days required",
        description: "Days must be a positive number.",
        variant: "destructive",
      });
      return;
    }

    try {
      setCompSubmitting(true);
      await applyCompensatoryEarn({
        employeeID,
        managerID,
        epmtID,
        name: user.name,
        fromDate: compFromDate,
        toDate: compToDate,
        days: requestedDays,
        reason: compReason || undefined,
      });
      toast({
        title: "Compensatory leave request submitted",
        description:
          "Your reporting person will review and approve. Once approved, the days will appear in your Compensatory balance.",
      });
      setIsCompApplyOpen(false);
      resetCompForm();
      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "object" && detail?.detail
          ? detail.detail
          : typeof detail === "string"
            ? detail
            : error?.message || "Failed to submit compensatory request";
      toast({
        title: "Failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setCompSubmitting(false);
    }
  };

  const handleCancelLeave = async (leaveId: string) => {
    try {
      await cancelLeave(leaveId);
      toast({
        title: "Success",
        description: "Leave request withdrawn successfully",
      });
      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "string" ? detail : error?.message || "Failed to withdraw leave";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleApproveReject = (
    leave: Leave,
    action: "approved" | "rejected"
  ) => {
    setSelectedLeave(leave);
    setApprovalAction(action);
    setApprovalComments("");
    resetDecisionState();
    setIsApprovalDialogOpen(true);

    // Only an approval has to answer the question — a rejection costs nobody
    // anything. Fetched rather than read off the list row because the shortfall
    // depends on what has been approved since this request was filed.
    if (action === "approved") {
      loadOverdraft(leave._id);
    }
  };

  const resetDecisionState = () => {
    setOverdraft(null);
    setDisposition(null);
    setDecisionOnly(false);
    setMakeupMonth("");
    setMakeupExact(false);
    setMakeupFrom("");
    setMakeupTo("");
  };

  const loadOverdraft = (leaveId: string) => {
    setOverdraftLoading(true);
    getOverdraftPreview(leaveId)
      .then((preview) => {
        setOverdraft(preview?.overdraws ? preview : null);
        // Preselect the first month whose payroll is still open, so the common
        // case is one click.
        if (preview?.suggested_month) setMakeupMonth(preview.suggested_month);
      })
      .catch(() => setOverdraft(null))
      .finally(() => setOverdraftLoading(false));
  };

  /** The period in whichever shape the approver filled in, or null. */
  const makeupPeriod = (): MakeupPeriod | null => {
    if (disposition !== "carry_forward") return null;
    if (makeupExact) {
      return makeupFrom && makeupTo
        ? { makeup_from: makeupFrom, makeup_to: makeupTo }
        : null;
    }
    return makeupMonth ? { makeup_month: makeupMonth } : null;
  };

  // The first day the hours may start counting: today, or — when the leave
  // itself falls in the current month — the day after it ended, since the rest
  // of that month is allowed and the days before the leave are not.
  const earliestMakeupDay =
    overdraft?.same_month_from || new Date().toISOString().slice(0, 10);

  // Reopens the same panel, in "decide" mode: the leave is already approved,
  // only what happens to the extra days is being changed.
  const handleChangeDisposition = (leave: Leave) => {
    setSelectedLeave(leave);
    setApprovalAction("approved");
    setApprovalComments("");
    resetDecisionState();
    setDecisionOnly(true);
    setIsApprovalDialogOpen(true);
    loadOverdraft(leave._id);
  };

  const handleApprovalSubmit = async () => {
    if (!selectedLeave) return;
    const leaveId = selectedLeave._id;
    try {

      const period = makeupPeriod();
      if (decisionOnly) {
        if (!disposition) return;
        const res = await updateOverdraftDisposition(leaveId, disposition, period);
        toast({
          title: "Updated",
          description: res?.note || "Decision changed.",
          duration: 7000,
        });
        setIsApprovalDialogOpen(false);
        setSelectedLeave(null);
        resetDecisionState();
        fetchLeaves();
        return;
      }

      await updateLeaveStatus(
        leaveId,
        approvalAction,
        approvalComments,
        disposition,
        period
      );

      toast({
        title: "Success",
        description:
          overdraft && disposition
            ? `Leave ${approvalAction} · ${overdraft.unpaid_days} day(s) ${
                disposition === "carry_forward"
                  ? `to be worked back (${overdraft.makeup_hours_required ?? 0} hours)`
                  : "approved without a deduction"
              }`
            : `Leave request ${approvalAction} successfully`,
        duration: 7000,
      });

      setIsApprovalDialogOpen(false);
      setSelectedLeave(null);
      setApprovalComments("");
      resetDecisionState();
      fetchLeaves();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      // The server refuses an over-entitlement approval that names no
      // disposition. Reaching here means the dialog did not know it was one
      // (the preview failed to load) — so show the choice rather than an error.
      if (detail && typeof detail === "object" && detail.code === "OVERDRAFT_DISPOSITION_REQUIRED") {
        setOverdraft({ ...detail, leave_id: leaveId });
        toast({
          title: "Choose how to settle it",
          description: detail.detail,
          duration: 8000,
        });
        return;
      }
      toast({
        title: "Error",
        description:
          (typeof detail === "string" ? detail : null) ||
          error?.message ||
          "Failed to update leave",
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
    }
  };

  const filteredLeaves = leaves.filter((leave) => {
    const statusMatch = filterStatus === "all" || leave.status === filterStatus;
    const typeMatch = filterType === "all" || leave.leaveType === filterType;
    return statusMatch && typeMatch;
  });

  // Reset paginated lists to page 1 whenever the (shared) filters change.
  // Pending Approvals isn't filtered, so its page is just clamped on render.
  useEffect(() => {
    setMyLeavesPage(1);
    setAllTeamPage(1);
  }, [filterStatus, filterType, filterEmployee]);

  // Distinct employees appearing in team leave requests — powers the Employee
  // filter's searchable list, so it always matches who's actually in the list.
  const teamEmployees = Array.from(
    allLeaves
      .reduce((map, leave) => {
        if (leave.employeeId) {
          // Prefer the full "First Last" name from the roster; fall back to the
          // name stored on the leave (first name only) if the id isn't found.
          map.set(
            leave.employeeId,
            employeeNameMap[leave.employeeId] ||
              leave.employeeName ||
              leave.employeeId,
          );
        }
        return map;
      }, new Map<string, string>())
      .entries(),
  )
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredAllLeaves = allLeaves.filter((leave) => {
    const statusMatch = filterStatus === "all" || leave.status === filterStatus;
    const typeMatch = filterType === "all" || leave.leaveType === filterType;
    const employeeMatch =
      filterEmployee === "all" || leave.employeeId === filterEmployee;
    return statusMatch && typeMatch && employeeMatch;
  });

  // Pending Approvals lists leaves awaiting THIS user's decision. A manager's
  // own leave routes up to their manager / admin, so it must never appear here
  // as actionable — you cannot approve your own request. (employeeID is the
  // logged-in user's own employee code; null for admins, who see everything.)
  const pendingLeaves = allLeaves.filter(
    (leave) => leave.status === "pending" && leave.employeeId !== employeeID
  );

  const sortedHolidays = [...holidays].sort((a, b) => a.date.localeCompare(b.date));

  const renderHolidaysTab = () => (
    <div className="space-y-4">
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PartyPopper className="h-5 w-5 text-blue-600" />
            Company Holidays
          </CardTitle>
          <CardDescription>
            {canEditHolidays
              ? "Add, edit, or remove the holidays observed by your company."
              : "Holidays observed by your company this year."}
          </CardDescription>
        </CardHeader>

        {canEditHolidays && (
          <CardContent className="space-y-3 pt-0">
            {editingHolidayKey && (
              <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 inline-block">
                Editing — update the date / name and click <em>Update Holiday</em>.
              </p>
            )}
            <div className="flex flex-col md:flex-row gap-2">
              <Input
                type="date"
                value={holidayDate}
                onChange={(e) => {
                  setHolidayDate(e.target.value);
                  setHolidayError("");
                }}
                className="md:flex-1"
              />
              <Input
                type="text"
                placeholder="Holiday name (e.g. Diwali)"
                value={holidayName}
                onChange={(e) => {
                  setHolidayName(e.target.value);
                  setHolidayError("");
                }}
                className="md:flex-1"
              />
              <Button
                onClick={handleSaveHoliday}
                disabled={holidaySaving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {holidaySaving
                  ? "Saving..."
                  : editingHolidayKey
                    ? "Update Holiday"
                    : (<><Plus className="h-4 w-4 mr-1" /> Add Holiday</>)}
              </Button>
              {editingHolidayKey && (
                <Button variant="outline" onClick={resetHolidayForm} disabled={holidaySaving}>
                  Cancel
                </Button>
              )}
            </div>
            {holidayError && (
              <p className="text-xs text-red-600">{holidayError}</p>
            )}
          </CardContent>
        )}
      </Card>

      {holidaysLoading ? (
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </CardContent>
        </Card>
      ) : sortedHolidays.length === 0 ? (
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarIcon className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No holidays added yet
            </h3>
            <p className="text-gray-600 text-center">
              {canEditHolidays
                ? "Use the form above to add your company's first holiday."
                : "Your company hasn't published any holidays yet."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {sortedHolidays.map((h) => {
            const isEditing = editingHolidayKey === h.date;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const holidayDateObj = new Date(h.date);
            holidayDateObj.setHours(0, 0, 0, 0);
            const dayDiff = Math.round(
              (holidayDateObj.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
            );
            const status: "today" | "upcoming" | "passed" =
              dayDiff === 0 ? "today" : dayDiff > 0 ? "upcoming" : "passed";
            const statusLabel =
              status === "today"
                ? "Today"
                : status === "upcoming"
                  ? dayDiff === 1
                    ? "Tomorrow"
                    : `In ${dayDiff} days`
                  : Math.abs(dayDiff) === 1
                    ? "Yesterday"
                    : `${Math.abs(dayDiff)} days ago`;
            const statusClass =
              status === "today"
                ? "bg-amber-100 text-amber-800 border-amber-200"
                : status === "upcoming"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : "bg-gray-100 text-gray-500 border-gray-200";
            return (
              <Card
                key={h.date}
                className={cn(
                  "bg-white/80 backdrop-blur-sm border-0 shadow-md hover:shadow-lg transition-all",
                  isEditing && "ring-2 ring-amber-300",
                  status === "passed" && "opacity-75",
                )}
              >
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                        status === "today"
                          ? "bg-amber-50 text-amber-600"
                          : status === "upcoming"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-gray-100 text-gray-400",
                      )}
                    >
                      <CalendarIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={cn(
                          "text-sm font-semibold truncate",
                          status === "passed" ? "text-gray-500" : "text-gray-900",
                        )}
                      >
                        {h.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(h.date), "EEEE, MMM dd, yyyy")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge
                      className={cn(
                        "border text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap",
                        statusClass,
                      )}
                    >
                      {status === "upcoming" ? "Upcoming · " : status === "passed" ? "Passed · " : ""}
                      {statusLabel}
                    </Badge>
                    {canEditHolidays && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditHoliday(h)}
                          disabled={holidaySaving}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveHoliday(h.date)}
                          disabled={holidaySaving}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Remove
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderLeaveCard = (
    leave: Leave,
    showApprovalButtons = false,
    showApplicantName = showApprovalButtons,
  ) => {
    const isCompEarn = leave.leaveType === "compensatory_earn";
    const isMatEarn = leave.leaveType === "maternity_earn";
    const isPatEarn = leave.leaveType === "paternity_earn";
    const isAnyEarn = isCompEarn || isMatEarn || isPatEarn;
    const earnTitle = isCompEarn
      ? "Compensatory Leave Request"
      : isMatEarn
        ? "Maternity Leave Request"
        : isPatEarn
          ? "Paternity Leave Request"
          : null;
    return (
    <Card
      key={leave._id}
      className={cn(
        "bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300",
        isAnyEarn && "ring-1 ring-emerald-200",
      )}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {getStatusIcon(leave.status)}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {earnTitle ?? `${leave.leaveType.replace("_", " ")} Leave`}
                </h3>
                {showApplicantName && leave.employeeName && (
                  <p className="flex items-center gap-1.5 text-sm text-gray-600">
                    {/* A leave row carries only the employee CODE (no user id),
                        so the photo is found through the employee-code bridge. */}
                    <PersonAvatar
                      employeeCode={leave.employeeId}
                      name={leave.employeeName}
                      size={24}
                    />
                    <span>by {leave.employeeName}</span>
                  </p>
                )}
              </div>
              {isAnyEarn && (
                <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200">
                  Earn request
                </Badge>
              )}
              {(leave.session === "first_half" || leave.session === "second_half") && (
                <Badge className="border bg-sky-50 text-sky-700 border-sky-200">
                  {leave.session === "first_half" ? "First half" : "Second half"}
                </Badge>
              )}
              <Badge className={cn("border", getStatusColor(leave.status))}>
                {leave.status?.charAt(0).toUpperCase() + leave.status.slice(1)}
              </Badge>
              {/* An approved leave that ran past the balance says so on the
                  card, with what it cost: the employee should not have to open
                  a payslip to find out. */}
              {!!leave.unpaidDays && (
                <Badge className="border bg-amber-50 text-amber-800 border-amber-200">
                  {overdraftChip(leave)}
                </Badge>
              )}
              {leave.conversion && (
                <Badge className="border bg-indigo-50 text-indigo-700 border-indigo-200">
                  {conversionChip(leave.conversion)}
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
              <div>
                <span className="font-medium">Duration:</span>
                <br />
                {format(new Date(leave.startDate), "MMM dd, yyyy")} -{" "}
                {format(new Date(leave.endDate), "MMM dd, yyyy")}
                <div className="text-xs text-gray-500 mt-1">
                  {leave.days} day{leave.days === 1 ? "" : "s"}
                  {isAnyEarn && " (grant if approved)"}
                </div>
              </div>

              <div>
                <span className="font-medium">Applied:</span>
                <br />
                {leave.appliedDate
                  ? format(new Date(leave.appliedDate), "MMM dd, yyyy")
                  : "—"}
              </div>

              {leave.approvedBy && (
                <div>
                  <span className="font-medium">
                    Processed:
                  </span>
                  <br />
                  <span className="text-xs text-gray-500">by {leave.approvedBy}</span>
                </div>
              )}
            </div>

            {leave.reason && (
              <div className="mt-3">
                <span className="font-medium text-sm text-gray-700">
                  Reason:
                </span>
                <p className="text-sm text-gray-600 mt-1">{leave.reason}</p>
              </div>
            )}

            {leave.managerComments && (
              <div className="mt-3">
                <span className="font-medium text-sm text-gray-700">
                  Manager Comments:
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  {leave.managerComments}
                </p>
              </div>
            )}
          </div>

          <div className="ml-4 flex flex-col gap-2">
            {showApprovalButtons && leave.status === "pending" && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleApproveReject(leave, "approved")}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleApproveReject(leave, "rejected")}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </>
            )}

            {/* Second thoughts on who pays. The split itself never moves — only
                the decision — and the server refuses it once that month's
                payroll has been approved.

                Gated on the right to approve rather than on
                `showApprovalButtons`, which every tab turns off for a leave
                that is no longer pending — so this button, and the obligation
                panel behind it, could not be reached at all. */}
            {(canApprove || canApproveFromTeam) &&
              leave.employeeId !== employeeID &&
              leave.status === "approved" &&
              !!leave.unpaidDays &&
              !!leave.overdraftDisposition && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangeDisposition(leave)}
                  className="text-amber-800 border-amber-200 hover:bg-amber-50"
                >
                  Change decision
                </Button>
              )}

            {!showApprovalButtons &&
              (leave.status === "pending" || leave.status === "approved") &&
              (employeeID
                ? leave.employeeId === employeeID
                : leave.employeeId === user?._id) &&
              (() => {
                if (!leave.startDate) return false;
                const start = new Date(leave.startDate);
                start.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                // Approved leaves stay withdrawable through the start date
                // itself; a still-pending one closes as soon as that day
                // arrives. Neither can be withdrawn afterwards.
                return leave.status === "approved"
                  ? today <= start
                  : today < start;
              })() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCancelLeave(leave._id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Withdraw
                </Button>
              )}
          </div>
        </div>
      </CardContent>
    </Card>
    );
  };

  // Shared page size for every paginated leaves list.
  const LEAVES_PAGE_SIZE = 10;

  // Task-panel-style pager: "Previous · numbered window of 5 · Next", with a
  // "Page X of Y" line on mobile. Matches the Task list (see Task/TaskList.tsx)
  // so the Pending Approvals and All Team Requests tabs look consistent.
  const renderPager = (
    currentPage: number,
    totalPages: number,
    onChange: (p: number) => void,
  ) => {
    if (totalPages <= 1) return null;
    return (
      <>
        <div className="mt-8 flex justify-center items-center gap-2">
          <button
            type="button"
            onClick={() => onChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="gt-btn is-ghost is-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>

          <div className="flex items-center gap-1.5">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;

              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => onChange(pageNum)}
                  className={currentPage === pageNum ? "gt-btn is-primary is-sm" : "gt-btn is-ghost is-sm"}
                  style={{ width: 36, height: 36, justifyContent: "center", padding: 0 }}
                >
                  <span className="gt-numerals">{pageNum}</span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => onChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="gt-btn is-ghost is-sm"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="text-center gt-page-sub gt-numerals md:hidden">
          Page {currentPage} of {totalPages}
        </div>
      </>
    );
  };

  // ── My Leaves: paginated list (10 per page) ─────────────────────────
  // Uses the shared Task-panel-style pager, same as the other leaves tabs.
  const renderMyLeavesPaginated = () => {
    const totalPages = Math.max(1, Math.ceil(filteredLeaves.length / LEAVES_PAGE_SIZE));
    const safePage = Math.min(myLeavesPage, totalPages);
    const pageItems = filteredLeaves.slice(
      (safePage - 1) * LEAVES_PAGE_SIZE,
      safePage * LEAVES_PAGE_SIZE,
    );
    return (
      <>
        <div className="grid gap-4">
          {pageItems.map((leave) => renderLeaveCard(leave, false))}
        </div>
        {renderPager(safePage, totalPages, setMyLeavesPage)}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
      {/* Header */}
      <div className="gt-page-hero flex-col md:flex-row gap-4 md:items-start">
        <div>
          <h1 className="gt-page-title">Leave <em>Management</em></h1>
          <p className="gt-page-sub" style={{ marginTop: 6 }}>
            {isManager
              ? <>Manage leave <em className="gt-serif-italic">requests</em> and view balances.</>
              : <>Manage your leave <em className="gt-serif-italic">requests</em> and view balances.</>}
          </p>
        </div>

        {/* Top-right action cluster — both Apply buttons live here so they
            sit together in the upper-right corner of the hero. */}
        <div className="md:ml-auto flex flex-col sm:flex-row gap-3 sm:items-start">

        {canApplyOwn && (
          <Dialog
            open={isApplyDialogOpen}
            onOpenChange={(open) => {
              setIsApplyDialogOpen(open);
              if (!open) resetApplyForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="gt-btn is-primary is-lg">
                <Plus className="h-4 w-4" />
                Apply for Leave
              </Button>
            </DialogTrigger>
            {/* Capped at 90vh with only the FORM scrolling, so the title and
                the Submit button stay put however tall the body gets — ticking
                "half day" adds a whole block, and on a short screen the dialog
                used to run off both ends of the viewport.
                `overflow-visible` was here for the date picker, which no longer
                needs it: the calendar renders through createPortal, outside
                this element entirely. */}
            <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:w-full sm:max-w-md max-h-[90vh] p-0 gap-0 flex flex-col">
              <DialogHeader className="shrink-0 px-4 pt-4 pb-3 sm:px-6 sm:pt-6">
                <DialogTitle>Apply for Leave</DialogTitle>
                <DialogDescription>
                  Submit a new leave request for approval
                </DialogDescription>
              </DialogHeader>

              {/* min-h-0 is what actually lets this scroll — without it a flex
                  child refuses to shrink below its content height. */}
              <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-1 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="leaveType">Leave Type</Label>
                  <Select
                    value={newLeave.leaveType}
                    onValueChange={(value) =>
                      setNewLeave({ ...newLeave, leaveType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select leave type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const balanceTypes = structuredBalance
                          ? (Object.keys(structuredBalance.balances) as LeaveType[])
                          : null;
                        const fallback: LeaveType[] = ["sick", "annual", "casual"];
                        const baseTypes: LeaveType[] = balanceTypes && balanceTypes.length
                          ? balanceTypes
                          : (() => {
                              const list: LeaveType[] = [...fallback];
                              if (leaveSettings?.maternity_enabled) list.push("maternity");
                              if (leaveSettings?.paternity_enabled) list.push("paternity");
                              if (leaveSettings?.compensatory_enabled) list.push("compensatory");
                              return list;
                            })();
                        const labels: Record<LeaveType, string> = {
                          sick: "Sick",
                          annual: "Annual",
                          casual: "Casual",
                          maternity: "Maternity",
                          paternity: "Paternity",
                          compensatory: "Compensatory",
                        };
                        return baseTypes.map((t) => {
                          const bal = structuredBalance?.balances[t];
                          const available =
                            typeof bal?.available === "number"
                              ? bal.available
                              : bal?.remaining;
                          // An allocated type with nothing left can still be
                          // applied for — those days are unpaid rather than
                          // refused — so the label says what will happen
                          // instead of a bare "0 available".
                          const overdrawable =
                            t === "sick" || t === "annual" || t === "casual";
                          const suffix =
                            typeof available !== "number"
                              ? ""
                              : available > 0
                              ? ` (${available} available)`
                              : overdrawable
                              ? " (nothing left — further days unpaid)"
                              : " (none available)";
                          return (
                            <SelectItem key={t} value={t}>
                              {labels[t]}
                              {suffix}
                            </SelectItem>
                          );
                        });
                      })()}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStartOpen(true)}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate
                        ? format(startDate, "MMM dd, yyyy")
                        : "Pick a date"}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!startDate || (canTakeHalfDay && isHalfDay)}
                      title={
                        canTakeHalfDay && isHalfDay
                          ? "A half day is a single date"
                          : !startDate
                            ? "Pick a start date first"
                            : undefined
                      }
                      onClick={() => setEndOpen(true)}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground",
                        (!startDate || (canTakeHalfDay && isHalfDay)) &&
                          "cursor-not-allowed opacity-60",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {!startDate
                        ? "Select start date first"
                        : endDate
                          ? format(endDate, "MMM dd, yyyy")
                          : "Pick a date"}
                    </Button>
                    {!startDate && (
                      <p className="text-[11px] text-gray-500">
                        Choose a start date to enable this.
                      </p>
                    )}
                    {startDate && canTakeHalfDay && isHalfDay && (
                      <p className="text-[11px] text-gray-500">
                        Same as the start date for a half day.
                      </p>
                    )}
                  </div>
                </div>

                {/* Half-day leave. Rendered only when the branch has configured
                    `leave.half_day` — the server decides, the form renders.
                    Ticking it forces the range to a single date, because half a
                    day is half of one day. */}
                {canTakeHalfDay && (
                  <div className="space-y-2 rounded-lg border border-[#e8e3d8] p-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-emerald-700"
                        checked={isHalfDay}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setIsHalfDay(on);
                          // Collapse the range to the start date: a half day
                          // across five days is not a thing anyone can be paid.
                          if (on && startDate) setEndDate(startDate);
                          setHalfDaySession(
                            on ? defaultSession(halfDayConfig) : "",
                          );
                        }}
                      />
                      <span className="text-sm font-medium">
                        Take this as a half day
                      </span>
                    </label>

                    {isHalfDay && (
                      <div className="pl-6 space-y-2">
                        {halfDayConfig.sessions.length > 1 && (
                          <div className="flex flex-wrap gap-3">
                            {halfDayConfig.sessions.map((s) => (
                              <label
                                key={s}
                                className="flex items-center gap-1.5 cursor-pointer text-sm"
                              >
                                <input
                                  type="radio"
                                  name="halfDaySession"
                                  className="h-3.5 w-3.5 accent-emerald-700"
                                  value={s}
                                  checked={halfDaySession === s}
                                  onChange={() => setHalfDaySession(s)}
                                />
                                {sessionLabel(s, halfDayConfig)}
                              </label>
                            ))}
                          </div>
                        )}
                        <p className="text-[11px] text-gray-500">
                          Counts as 0.5 days against your balance.{" "}
                          {expectedAtWorkText(halfDayConfig, halfDaySession)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <Textarea
                    id="reason"
                    placeholder="Enter reason for leave..."
                    value={newLeave.reason}
                    onChange={(e) =>
                      setNewLeave({ ...newLeave, reason: e.target.value })
                    }
                  />
                </div>
              </div>

              <DialogFooter className="shrink-0 gap-2 border-t border-[#e8e3d8] px-4 py-3 sm:px-6 sm:py-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsApplyDialogOpen(false);
                    resetApplyForm();
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleApplyLeave}>Submit Application</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Maternity-earn request — same earn-then-spend pattern as the
            compensatory flow. Only shown to female employees on enabled
            branches. Backend re-validates eligibility on submit. */}
        {leaveSettings?.maternity_enabled &&
          userGender === "female" &&
          canApplyOwn && (
            <Dialog
              open={isMatApplyOpen}
              onOpenChange={(open) => {
                setIsMatApplyOpen(open);
                if (!open) resetMatForm();
              }}
            >
              <DialogTrigger asChild>
                <Button className="gt-btn is-primary is-lg">
                  <Gift className="h-4 w-4" />
                  Request Maternity
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>Request Maternity Leave</DialogTitle>
                  <DialogDescription>
                    Request your maternity entitlement. Your reporting person
                    will review and approve. Once approved, the days will
                    appear in your Maternity balance for you to apply against.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="mat-from">From</Label>
                      <Input
                        id="mat-from"
                        type="date"
                        value={matFromDate}
                        onChange={(e) => {
                          setMatFromDate(e.target.value);
                          const auto = daysBetween(e.target.value, matToDate);
                          if (auto > 0) setMatDays(String(auto));
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mat-to">To</Label>
                      <Input
                        id="mat-to"
                        type="date"
                        value={matToDate}
                        onChange={(e) => {
                          setMatToDate(e.target.value);
                          const auto = daysBetween(matFromDate, e.target.value);
                          if (auto > 0) setMatDays(String(auto));
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mat-days">Total days</Label>
                    <Input
                      id="mat-days"
                      type="number"
                      min={1}
                      value={matDays}
                      onChange={(e) => setMatDays(e.target.value)}
                      placeholder="e.g. 90"
                    />
                    <p className="text-[11px] text-gray-500">
                      Auto-calculated from the dates above. You can override
                      it if your entitlement differs.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mat-reason">
                      Note <span className="text-gray-400 text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      id="mat-reason"
                      placeholder="Anything your reporting person should know…"
                      value={matReason}
                      onChange={(e) => setMatReason(e.target.value)}
                    />
                  </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsMatApplyOpen(false);
                      resetMatForm();
                    }}
                    disabled={matSubmitting}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleApplyParentalEarn("maternity")}
                    disabled={matSubmitting}
                    className="gt-btn is-primary w-full sm:w-auto"
                  >
                    {matSubmitting ? "Submitting…" : "Submit Request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

        {/* Paternity-earn request — mirrors the maternity dialog. Only
            shown to male employees on enabled branches. */}
        {leaveSettings?.paternity_enabled &&
          userGender === "male" &&
          canApplyOwn && (
            <Dialog
              open={isPatApplyOpen}
              onOpenChange={(open) => {
                setIsPatApplyOpen(open);
                if (!open) resetPatForm();
              }}
            >
              <DialogTrigger asChild>
                <Button className="gt-btn is-primary is-lg">
                  <Gift className="h-4 w-4" />
                  Request Paternity
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>Request Paternity Leave</DialogTitle>
                  <DialogDescription>
                    Request your paternity entitlement. Your reporting person
                    will review and approve. Once approved, the days will
                    appear in your Paternity balance for you to apply against.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="pat-from">From</Label>
                      <Input
                        id="pat-from"
                        type="date"
                        value={patFromDate}
                        onChange={(e) => {
                          setPatFromDate(e.target.value);
                          const auto = daysBetween(e.target.value, patToDate);
                          if (auto > 0) setPatDays(String(auto));
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pat-to">To</Label>
                      <Input
                        id="pat-to"
                        type="date"
                        value={patToDate}
                        onChange={(e) => {
                          setPatToDate(e.target.value);
                          const auto = daysBetween(patFromDate, e.target.value);
                          if (auto > 0) setPatDays(String(auto));
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pat-days">Total days</Label>
                    <Input
                      id="pat-days"
                      type="number"
                      min={1}
                      value={patDays}
                      onChange={(e) => setPatDays(e.target.value)}
                      placeholder="e.g. 15"
                    />
                    <p className="text-[11px] text-gray-500">
                      Auto-calculated from the dates above. You can override
                      it if your entitlement differs.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pat-reason">
                      Note <span className="text-gray-400 text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      id="pat-reason"
                      placeholder="Anything your reporting person should know…"
                      value={patReason}
                      onChange={(e) => setPatReason(e.target.value)}
                    />
                  </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsPatApplyOpen(false);
                      resetPatForm();
                    }}
                    disabled={patSubmitting}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleApplyParentalEarn("paternity")}
                    disabled={patSubmitting}
                    className="gt-btn is-primary w-full sm:w-auto"
                  >
                    {patSubmitting ? "Submitting…" : "Submit Request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

        {/* Compensatory-earn request: separate dialog, shown only when the
            feature is enabled on this branch. Asks the manager to grant a
            comp leave because the employee worked on a holiday. */}
        {leaveSettings?.compensatory_enabled &&
          canApplyOwn && (
            <Dialog
              open={isCompApplyOpen}
              onOpenChange={(open) => {
                setIsCompApplyOpen(open);
                if (!open) resetCompForm();
              }}
            >
              <DialogTrigger asChild>
                <Button className="gt-btn is-primary is-lg">
                  <Gift className="h-4 w-4" />
                  Request Compensatory
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-2rem)] max-w-md sm:w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>Request Compensatory Leave</DialogTitle>
                  <DialogDescription>
                    Request compensatory days for working on holidays. Your
                    reporting person will review and approve. Once approved,
                    the days will appear in your Compensatory balance for you
                    to apply against.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="comp-from">From</Label>
                      <Input
                        id="comp-from"
                        type="date"
                        value={compFromDate}
                        onChange={(e) => {
                          setCompFromDate(e.target.value);
                          const auto = daysBetween(e.target.value, compToDate);
                          if (auto > 0) setCompDays(String(auto));
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="comp-to">To</Label>
                      <Input
                        id="comp-to"
                        type="date"
                        value={compToDate}
                        onChange={(e) => {
                          setCompToDate(e.target.value);
                          const auto = daysBetween(compFromDate, e.target.value);
                          if (auto > 0) setCompDays(String(auto));
                        }}
                      />
                    </div>
                  </div>

                  {compLockedMonths.length > 0 && (
                    <p className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                      The salary for {compLockedLabel} is already processed, so
                      compensatory leave can no longer be claimed for those
                      days.
                    </p>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="comp-days">Total days</Label>
                    <Input
                      id="comp-days"
                      type="number"
                      min={1}
                      value={compDays}
                      onChange={(e) => setCompDays(e.target.value)}
                      placeholder="e.g. 2"
                    />
                    <p className="text-[11px] text-gray-500">
                      Auto-calculated from the dates above. You can override
                      it if it differs.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-reason">
                      Note <span className="text-gray-400 text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      id="comp-reason"
                      placeholder="Anything your reporting person should know…"
                      value={compReason}
                      onChange={(e) => setCompReason(e.target.value)}
                    />
                  </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCompApplyOpen(false);
                      resetCompForm();
                    }}
                    disabled={compSubmitting}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApplyCompensatory}
                    disabled={compSubmitting || compLockedMonths.length > 0}
                    className="gt-btn is-primary w-full sm:w-auto"
                  >
                    {compSubmitting ? "Submitting…" : "Submit Request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

        </div>
        {/* /top-right action cluster */}

        {/* Centered date-picker overlay — portaled to document.body so it
            always sits above the Apply Leave dialog (which uses its own
            Radix portal) and is centered in the viewport regardless of
            where the trigger sits inside the form. */}
        {(startOpen || endOpen) && createPortal(
          (
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
              // Radix Dialog (the Apply Leave dialog) sets pointer-events:
              // none on <body> while it's open. Because this portal is a
              // child of <body>, we have to opt back in explicitly or the
              // calendar day clicks are eaten.
              style={{ zIndex: 9999, pointerEvents: 'auto' }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                setStartOpen(false);
                setEndOpen(false);
              }}
            >
              <div
                className="bg-white rounded-xl shadow-2xl p-2"
                style={{ minWidth: 300, pointerEvents: 'auto' }}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-3 pt-2 pb-1">
                  <span className="text-sm font-semibold text-gray-800">
                    {startOpen ? 'Pick start date' : 'Pick end date'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setStartOpen(false);
                      setEndOpen(false);
                    }}
                    aria-label="Close"
                    className="p-1 rounded hover:bg-gray-100 text-gray-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {startOpen ? (
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setStartOpen(false);
                      // A half day is pinned to one date, so the end follows
                      // the start rather than being left behind on the old one.
                      if (date && canTakeHalfDay && isHalfDay) {
                        setEndDate(date);
                      } else if (date && endDate && endDate < date) {
                        setEndDate(undefined);
                      }
                    }}
                    initialFocus
                  />
                ) : (
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      if (date && startDate && date < startDate) return;
                      setEndDate(date);
                      setEndOpen(false);
                    }}
                    disabled={startDate ? { before: startDate } : undefined}
                    defaultMonth={startDate}
                    initialFocus
                  />
                )}
              </div>
            </div>
          ),
          document.body,
        )}
      </div>

      {/* Leave Balance Cards */}
      {structuredBalance && Object.keys(structuredBalance.balances).length > 0 && (
        <div className="space-y-3">
        {structuredBalance.leave_year?.start && structuredBalance.leave_year?.end && (() => {
          // Which year these balances count against, and when they reset —
          // the mobile app shows the same under its balance cards.
          const start = new Date(`${structuredBalance.leave_year.start}T00:00:00`);
          const end = new Date(`${structuredBalance.leave_year.end}T00:00:00`);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const daysLeft = differenceInDays(end, today) + 1;
          const sameYear = start.getFullYear() === end.getFullYear();
          const span = `${format(start, sameYear ? "d MMM" : "d MMM yyyy")} – ${format(end, "d MMM yyyy")}`;
          return (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2" title="Balances below count against this leave year">
                <span
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full"
                  style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
                >
                  <CalendarIcon className="h-4 w-4" />
                </span>
                <div className="leading-tight">
                  <div className="gt-stat-label">Leave year</div>
                  <div className="text-sm font-medium">{span}</div>
                </div>
              </div>
              {daysLeft > 0 && daysLeft <= 366 && (
                <span
                  className="rounded-full px-3 py-1 text-xs font-medium"
                  style={{ background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)" }}
                  title={`The next leave year starts on ${format(new Date(end.getTime() + 86400000), "d MMM yyyy")}`}
                >
                  {daysLeft} {daysLeft === 1 ? "day" : "days"} left · resets {format(new Date(end.getTime() + 86400000), "d MMM")}
                </span>
              )}
            </div>
          );
        })()}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(Object.entries(structuredBalance.balances) as [LeaveType, any][]).map(
            ([type, entry]) => (
              <div key={type} className="gt-stat">
                <div className="gt-stat-label" style={{ textTransform: "capitalize" }}>
                  {type}
                </div>
                {/* Negative is a real state now: days past entitlement are
                    unpaid leave, so the number says -2 rather than 0 and the
                    colour is what makes it impossible to read as "none left". */}
                <p
                  className="gt-stat-value"
                  style={entry.remaining < 0 ? { color: "#b45309" } : undefined}
                >
                  {entry.remaining}
                </p>
                <p className="gt-page-sub" style={{ fontSize: 12, marginTop: 2 }}>
                  {entry.remaining < 0 ? (
                    <span style={{ color: "#b45309" }}>
                      {Math.abs(entry.remaining)} day
                      {Math.abs(entry.remaining) === 1 ? "" : "s"} beyond your{" "}
                      {entry.allocated} days
                    </span>
                  ) : (
                    <>of {entry.allocated} days</>
                  )}
                  {entry.reserved_pending > 0
                    ? ` · ${entry.reserved_pending} pending`
                    : ""}
                </p>
                {/* Earned month by month (leave.accrual): without this line the
                    card would read "2.5 of 2.5 days" in March and look like the
                    year's 10 days had been cut, rather than still arriving. */}
                {entry.accrues_monthly && (
                  <p className="gt-page-sub" style={{ fontSize: 11, marginTop: 2 }}>
                    earned so far · {entry.entitled} a year, month by month
                  </p>
                )}
              </div>
            )
          )}
        </div>
        </div>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={isApprovalDialogOpen}
        onOpenChange={setIsApprovalDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {approvalAction === "approved" ? "Approve" : "Reject"} Leave
              Request
            </DialogTitle>
            <DialogDescription>
              {selectedLeave && (
                <>
                  {approvalAction === "approved" ? "Approve" : "Reject"} leave
                  request from {selectedLeave.employeeName} for{" "}
                  {selectedLeave.leaveType} leave from{" "}
                  {format(new Date(selectedLeave.startDate), "MMM dd, yyyy")} to{" "}
                  {format(new Date(selectedLeave.endDate), "MMM dd, yyyy")}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Days this request runs past the employee's entitlement. The
                approver has to say which currency pays for them before the
                approval goes through — both answers cost the employee
                something real, so neither is safe to assume for them. */}
            {approvalAction === "approved" && overdraftLoading && (
              <p className="gt-page-sub" style={{ fontSize: 12 }}>
                Checking the leave balance…
              </p>
            )}
            {approvalAction === "approved" && overdraft && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-3">
                <div className="flex items-start gap-2 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    <strong>{overdraft.unpaid_days}</strong> day
                    {overdraft.unpaid_days === 1 ? "" : "s"} of this request
                    {overdraft.covered_days
                      ? ` (of ${overdraft.charged_days})`
                      : ""}{" "}
                    are beyond {selectedLeave?.employeeName || "this employee"}'s{" "}
                    {overdraft.leave_type} leave balance.
                    {overdraft.unpaid_dates?.length ? (
                      <span className="block mt-1 text-xs">
                        {overdraft.unpaid_dates
                          .map((d) => format(new Date(`${d}T00:00:00`), "d MMM"))
                          .join(", ")}
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="space-y-2">
                  {([
                    {
                      value: "no_deduction" as OverdraftDisposition,
                      label: "No deduction",
                      hint: "Treated as an ordinary leave. Nothing is recovered — no salary cut, no hours to work back.",
                      disabled: false,
                    },
                    {
                      value: "carry_forward" as OverdraftDisposition,
                      label: "Carry forward — work the hours back",
                      hint:
                        overdraft.makeup_available === false
                          ? "Unavailable: overtime is not enabled for this employee, so they would have no way to work the hours back."
                          : `${overdraft.makeup_hours_required ?? 0} hours inside a period you choose — or the same debt in overtime earnings, which is theirs to pick afterwards. Whatever is left at the end of the period is deducted from that month's salary, and the hours that count towards the leave are not also paid as overtime.`,
                      disabled: overdraft.makeup_available === false,
                    },
                  ]).map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start gap-2 rounded-md border p-2 text-sm ${
                        option.disabled
                          ? "cursor-not-allowed border-amber-100 opacity-60"
                          : "cursor-pointer " +
                            (disposition === option.value
                              ? "border-amber-500 bg-white"
                              : "border-amber-200")
                      }`}
                    >
                      <input
                        type="radio"
                        name="overdraft-disposition"
                        className="mt-1"
                        disabled={option.disabled}
                        checked={disposition === option.value}
                        onChange={() => setDisposition(option.value)}
                      />
                      <span>
                        <span className="font-medium">{option.label}</span>
                        <span className="block text-xs text-amber-900/80">
                          {option.hint}
                        </span>
                      </span>
                    </label>
                  ))}

                  {/* The period. A month is what people say out loud; exact
                      dates are there for the cases a month cannot express.
                      Normally it cannot start in the past — hours already
                      worked cannot pay for a leave taken later — the one
                      exception being the leave's OWN month, which may be used
                      from the day after the leave ended. The server refuses a
                      period ending in a month whose payroll is already out. */}
                  {disposition === "carry_forward" && (
                    <div className="rounded-md border border-amber-200 bg-white p-2 space-y-2">
                      <Label className="text-xs">Hours to be worked back in</Label>
                      {makeupExact ? (
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            aria-label="Make-up period start"
                            value={makeupFrom}
                            min={earliestMakeupDay}
                            onChange={(e) => setMakeupFrom(e.target.value)}
                          />
                          <Input
                            type="date"
                            aria-label="Make-up period end"
                            value={makeupTo}
                            min={makeupFrom || earliestMakeupDay}
                            onChange={(e) => setMakeupTo(e.target.value)}
                          />
                        </div>
                      ) : (
                        <select
                          aria-label="Make-up month"
                          className="w-full rounded-md border px-2 py-1 text-sm"
                          value={makeupMonth}
                          onChange={(e) => setMakeupMonth(e.target.value)}
                        >
                          {monthOptions(
                            overdraft.suggested_month,
                            overdraft.same_month_from
                          ).map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      )}
                      {overdraft.same_month_from ? (
                        <p className="text-xs text-amber-900/80">
                          This leave is in the current month, so it can be
                          worked back in the rest of it — from{" "}
                          {format(
                            new Date(`${overdraft.same_month_from}T00:00:00`),
                            "d MMM"
                          )}{" "}
                          to the end of the month.
                        </p>
                      ) : null}
                      <button
                        type="button"
                        className="text-xs underline text-amber-900"
                        onClick={() => setMakeupExact((v) => !v)}
                      >
                        {makeupExact ? "Use a whole month" : "Use exact dates"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Where an already-decided carry-forward stands. */}
            {decisionOnly && overdraft?.obligation && (
              <div className="rounded-lg border bg-gray-50 p-3 text-sm">
                {/* Stated in the unit the EMPLOYEE chose. They may be working
                    off an AMOUNT of overtime earnings rather than hours, and
                    reporting their hours instead shows a debt that stands
                    still while the one they are paying comes down. */}
                <div className="font-medium">
                  {overdraft.obligation.recovery_choice === "money"
                    ? `${rupees(overdraft.obligation.amount_done)} of ${rupees(
                        overdraft.obligation.amount_required
                      )} earned back`
                    : `${overdraft.obligation.hours_done} of ${overdraft.obligation.hours_required} hours worked back`}
                </div>
                <div className="text-xs text-gray-600">
                  {overdraft.obligation.state === "open"
                    ? `${overdraft.obligation.days_remaining_in_period} day(s) left in the period, ending ${overdraft.obligation.to}`
                    : `Settled${
                        overdraft.obligation.shortfall_days
                          ? ` — ${overdraft.obligation.shortfall_days} day(s) deducted`
                          : " — worked back in full"
                      }`}
                </div>
                {overdraft.obligation.recovery_choice === "money" ? (
                  <div className="text-xs text-gray-600">
                    They chose to settle in overtime earnings, which at a rate
                    above 1x takes fewer hours than the{" "}
                    {overdraft.obligation.hours_required} hours the leave is
                    worth. {overdraft.obligation.hours_done} hours put in so
                    far.
                  </div>
                ) : null}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="comments">
                Comments{" "}
                {approvalAction === "rejected" && (
                  <span className="text-red-500"></span>
                )}
              </Label>
              <Textarea
                id="comments"
                placeholder={
                  approvalAction === "approved"
                    ? "Add any comments (optional)..."
                    : "Please provide a reason for rejection (optional)..."
                }
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                className="min-h-20"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsApprovalDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprovalSubmit}
              // An over-entitlement approval with no choice made would be
              // refused by the server anyway; disabling says so up front.
              disabled={
                approvalAction === "approved" &&
                (overdraftLoading ||
                  (!!overdraft && !disposition) ||
                  (disposition === "carry_forward" && !makeupPeriod()))
              }
              className={
                approvalAction === "approved"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {decisionOnly
                ? "Save decision"
                : approvalAction === "approved"
                  ? "Approve"
                  : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      {isTeamViewer ? (
        // <Tabs defaultValue="pending" className="space-y-6">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value)}
          className="space-y-6"
        >
          <TabsList
            className="grid w-full"
            style={{
              gridTemplateColumns: `repeat(${
                [showApprovalsTab, showTeamTab, showMyTab, showHolidaysTab].filter(Boolean).length || 1
              }, minmax(0, 1fr))`,
            }}
          >
            {showApprovalsTab && (
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Pending Approvals ({pendingLeaves.length})
              </TabsTrigger>
            )}

            {showTeamTab && (
              <TabsTrigger value="all">All Team Requests</TabsTrigger>
            )}

            {showMyTab && <TabsTrigger value="my">My Leaves</TabsTrigger>}

            {showHolidaysTab && (
              <TabsTrigger value="holidays" className="flex items-center gap-2">
                <PartyPopper className="h-4 w-4" />
                Holidays
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                  Pending Leave Requests
                </CardTitle>
                <CardDescription>
                  Review and approve or reject leave requests from your team
                  members
                </CardDescription>
              </CardHeader>
            </Card>

            {pendingLeaves.length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No pending requests
                  </h3>
                  <p className="text-gray-600 text-center">
                    All leave requests have been processed.
                  </p>
                </CardContent>
              </Card>
            ) : (
              (() => {
                const totalPages = Math.max(1, Math.ceil(pendingLeaves.length / LEAVES_PAGE_SIZE));
                const safePage = Math.min(pendingPage, totalPages);
                const pageItems = pendingLeaves.slice(
                  (safePage - 1) * LEAVES_PAGE_SIZE,
                  safePage * LEAVES_PAGE_SIZE,
                );
                return (
                  <>
                    <div className="grid gap-4">
                      {pageItems.map((leave) => renderLeaveCard(leave, canApprove))}
                    </div>
                    {renderPager(safePage, totalPages, setPendingPage)}
                  </>
                );
              })()
            )}
          </TabsContent>

          <TabsContent value="all" className="space-y-4">
            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={filterStatus}
                      onValueChange={setFilterStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="sick">Sick</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        {leaveSettings?.paternity_enabled && (
                          <SelectItem value="paternity">Paternity</SelectItem>
                        )}
                        {leaveSettings?.maternity_enabled && (
                          <SelectItem value="maternity">Maternity</SelectItem>
                        )}
                        {leaveSettings?.compensatory_enabled && (
                          <SelectItem value="compensatory">Compensatory</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Employee</Label>
                    <Popover
                      open={employeeFilterOpen}
                      onOpenChange={setEmployeeFilterOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={employeeFilterOpen}
                          className="w-full justify-between font-normal"
                        >
                          <span className="truncate">
                            {filterEmployee === "all"
                              ? "All Employees"
                              : teamEmployees.find(
                                  (e) => e.id === filterEmployee,
                                )?.name ?? "All Employees"}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[--radix-popover-trigger-width] p-0"
                        align="start"
                      >
                        <Command>
                          <CommandInput placeholder="Search employee..." />
                          {/* max-h fits ~5 rows (5 x h-11 + group padding); rest scroll. */}
                          <CommandList className="max-h-[236px]">
                            <CommandEmpty>No employee found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="All Employees"
                                className="h-11"
                                onSelect={() => {
                                  setFilterEmployee("all");
                                  setEmployeeFilterOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4 shrink-0",
                                    filterEmployee === "all"
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                All Employees
                              </CommandItem>
                              {teamEmployees.map((emp) => (
                                <CommandItem
                                  key={emp.id}
                                  value={emp.name}
                                  className="h-11"
                                  onSelect={() => {
                                    setFilterEmployee(emp.id);
                                    setEmployeeFilterOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4 shrink-0",
                                      filterEmployee === emp.id
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  {/* emp.id is the leave rows' employee CODE (the
                                      filter compares it to leave.employeeId), not
                                      Employee.id, so it bridges by code. */}
                                  <PersonAvatar
                                    employeeCode={emp.id}
                                    name={emp.name}
                                    size={20}
                                  />
                                  {emp.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                All Team Leave Requests
              </h2>

              {filteredAllLeaves.length === 0 ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No leave requests found
                    </h3>
                    <p className="text-gray-600 text-center">
                      No leave requests match your current filters.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                (() => {
                  const totalPages = Math.max(1, Math.ceil(filteredAllLeaves.length / LEAVES_PAGE_SIZE));
                  const safePage = Math.min(allTeamPage, totalPages);
                  const pageItems = filteredAllLeaves.slice(
                    (safePage - 1) * LEAVES_PAGE_SIZE,
                    safePage * LEAVES_PAGE_SIZE,
                  );
                  return (
                    <>
                      <div className="grid gap-4">
                        {pageItems.map((leave) =>
                          renderLeaveCard(
                            leave,
                            // No approve/reject on your OWN leave — it routes up to
                            // your manager / admin, not back to you.
                            canApproveFromTeam &&
                              leave.status === "pending" &&
                              leave.employeeId !== employeeID,
                            true,
                          )
                        )}
                      </div>
                      {renderPager(safePage, totalPages, setAllTeamPage)}
                    </>
                  );
                })()
              )}
            </div>
          </TabsContent>

          <TabsContent value="my" className="space-y-4">
            {/* Leave Balance Cards for managers */}
            {user?.role === "manager" && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(leaveBalance).map(([type, balance]) => (
                  <Card
                    key={type}
                    className="bg-white/80 backdrop-blur-sm border-0 shadow-lg"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600 capitalize">
                        {type.replace("_", " ")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-gray-900">
                        {balance}
                      </div>
                      <p className="text-xs text-gray-500">days remaining</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={filterStatus}
                      onValueChange={setFilterStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="sick">Sick</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        {leaveSettings?.paternity_enabled &&
                          userGender === "male" && (
                          <SelectItem value="paternity">Paternity</SelectItem>
                        )}
                        {leaveSettings?.maternity_enabled &&
                          userGender === "female" && (
                          <SelectItem value="maternity">Maternity</SelectItem>
                        )}
                        {leaveSettings?.compensatory_enabled && (
                          <SelectItem value="compensatory">Compensatory</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                My Leave Requests
              </h2>

              {tabLoading ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex items-center justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </CardContent>
                </Card>
              ) : filteredLeaves.length === 0 ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No leave requests found
                    </h3>
                    <p className="text-gray-600 text-center">
                      {leaves.length === 0
                        ? "You haven't submitted any leave requests yet."
                        : "No leave requests match your current filters."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                renderMyLeavesPaginated()
              )}
            </div>
          </TabsContent>

          <TabsContent value="holidays" className="space-y-4">
            {renderHolidaysTab()}
          </TabsContent>
        </Tabs>
      ) : (
        // Employee view — own leaves + read-only holidays
        <Tabs
          value={activeTab === "holidays" || !showMyTab ? "holidays" : "my"}
          onValueChange={(value) => setActiveTab(value)}
          className="space-y-6"
        >
          <TabsList
            className="grid w-full"
            style={{
              gridTemplateColumns: `repeat(${
                [showMyTab, showHolidaysTab].filter(Boolean).length || 1
              }, minmax(0, 1fr))`,
            }}
          >
            {showMyTab && <TabsTrigger value="my">My Leaves</TabsTrigger>}
            {showHolidaysTab && (
              <TabsTrigger value="holidays" className="flex items-center gap-2">
                <PartyPopper className="h-4 w-4" />
                Holidays
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="my" className="space-y-4">
            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="sick">Sick</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        {leaveSettings?.paternity_enabled &&
                          userGender === "male" && (
                          <SelectItem value="paternity">Paternity</SelectItem>
                        )}
                        {leaveSettings?.maternity_enabled &&
                          userGender === "female" && (
                          <SelectItem value="maternity">Maternity</SelectItem>
                        )}
                        {leaveSettings?.compensatory_enabled && (
                          <SelectItem value="compensatory">Compensatory</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Leave Requests List */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Your Leave Requests
              </h2>

              {filteredLeaves.length === 0 ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No leave requests found
                    </h3>
                    <p className="text-gray-600 text-center">
                      {leaves.length === 0
                        ? "You haven't submitted any leave requests yet."
                        : "No leave requests match your current filters."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                renderMyLeavesPaginated()
              )}
            </div>
          </TabsContent>

          <TabsContent value="holidays" className="space-y-4">
            {renderHolidaysTab()}
          </TabsContent>
        </Tabs>
      )}

      {/* Apply error / insufficient-balance popup */}
      <Dialog
        open={applyError.open}
        onOpenChange={(open) => setApplyError((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cannot submit leave</DialogTitle>
            <DialogDescription>{applyError.message}</DialogDescription>
          </DialogHeader>
          {typeof applyError.balance_remaining === "number" && (
            <div className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <strong>{applyError.balance_remaining}</strong>{" "}
              day{applyError.balance_remaining === 1 ? "" : "s"} of{" "}
              {applyError.leave_type || "this type"} leave remaining.
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setApplyError({ open: false, message: "" })}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
